"use client";

import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, Award, Clock } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const features = [{
  icon: Award,
  title: "New Builds",
  description: "Complete new construction solutions for industrial, commercial and residential projects tailored to your operational requirements"
}, {
  icon: Award,
  title: "Fitouts",
  description: "Complete commercial and industrial interior solutions tailored to operational needs, including offices, warehouses, and retail spaces."
}, {
  icon: ShieldCheck,
  title: "Remedial Works",
  description: "Specialists in structural repairs, waterproofing, and facade restoration to extend asset life and ensure building compliance."
}, {
  icon: Clock,
  title: "Defect Management",
  description: "Comprehensive identification and rectification services using structured processes and detailed reporting for quality assurance."
}, {
  icon: ShieldCheck,
  title: "Quality Control",
  description: "Implement rigorous quality assurance processes to ensure project standards are met."
}, {
  icon: ShieldCheck,
  title: "Work Health & Safety",
  description: "Maintain safe work environments and compliance with WHS regulations."
}];
export default function WhyChooseAnnabriel() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="225520" id="strategic-construction-capabilities" className="bg-black py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={`mx-auto max-w-2xl text-center transition-all duration-700 ease-out ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="font-default text-3xl font-extrabold tracking-widest text-white uppercase sm:text-4xl">
            Strategic Capabilities
          </h2>
          <p className="font-default mt-6 text-lg leading-8 text-slate-400">
            Bottom Up Projects delivers high-quality construction solutions across offices, 
            warehouses, and mixed-use developments with a focus on precision and safety.
          </p>
        </div>

        <div ref={gridRef} className={`mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none transition-all duration-1000 ease-out delay-200 ${isGridVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          <div className="grid max-w-xl grid-cols-1 gap-8 lg:max-w-none lg:grid-cols-3">
            {features.map((feature, i) => {
            const Icon = feature.icon;
            return <Card key={i} data-index={i} className="group border-primary/20 bg-slate-900/40 shadow-none transition-all duration-300 hover:border-primary hover:bg-slate-900/60">
                  <CardHeader className="gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-primary/10 transition-colors duration-300 group-hover:bg-primary/20">
                      <Icon className="h-6 w-6 text-primary transition-transform duration-300 group-hover:scale-110" aria-hidden="true" />
                    </div>
                    <div>
                      <CardTitle className="font-default text-xl font-bold text-white uppercase tracking-wider transition-colors duration-300 group-hover:text-primary">
                        {feature.title}
                      </CardTitle>
                      <CardDescription className="font-default mt-2 text-base leading-7 text-slate-400">
                        {feature.description}
                      </CardDescription>
                    </div>
                  </CardHeader>
                </Card>;
          })}
          </div>
        </div>
      </div>
    </section>;
}